import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Wallet } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { MobileScanQrButton } from '../../components/MobileScanQrButton';
import {
  ExpenseReportCreateDrawer,
  ExpenseReportDrawer,
} from '../../components/ExpenseDrawers';
import { useExpenseReports, type ExpenseReport } from '../../hooks/useExpenses';
import { QueryError } from '../../components/ui/QueryError';
import { EmptyState } from '../../components/ui/EmptyState';
import { formatAmount, formatDate } from '../../lib/format';
import { useCan } from '../../hooks/useCan';

const STATUS_TONE: Record<ExpenseReport['status'], 'neutral' | 'accent' | 'success' | 'warn' | 'muted'> = {
  draft: 'muted',
  submitted: 'accent',
  approved: 'success',
  rejected: 'warn',
  paid: 'success',
};

export function ExpensesPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const { canMutate } = useCan();
  const { data: reports, isLoading, isError, refetch } = useExpenseReports(wsId);
  const [createOpen, setCreateOpen] = useState(false);
  const [drawerId, setDrawerId] = useState<string | null>(null);

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-7xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="display text-2xl font-semibold">{t('expenses.title')}</h1>
          <div className="flex items-center gap-3">
            {wsId && <MobileScanQrButton workspaceId={wsId} />}
            {canMutate && (
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-foreground text-background hover:bg-foreground/90 text-sm font-medium"
              >
                <Plus size={14} />
                {t('expenses.newReport')}
              </button>
            )}
          </div>
        </div>
        <Card>
          {isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : isError ? (
            <QueryError onRetry={refetch} />
          ) : !reports || reports.length === 0 ? (
            <EmptyState
              icon={Wallet}
              title={t('emptyState.expenses.title')}
              description={t('emptyState.expenses.body')}
              primaryLabel={t('expenses.newReport')}
              primaryIcon={Plus}
              onPrimary={() => setCreateOpen(true)}
              offerSampleData
              wsId={wsId}
            />
          ) : (
            <ul className="divide-y divide-ink-200 dark:divide-ink-800">
              {reports.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setDrawerId(r.id)}
                    className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-ink-50 dark:hover:bg-ink-900 transition-colors"
                  >
                    <div>
                      <div className="font-medium">{r.title}</div>
                      <div className="text-xs text-ink-500 mt-0.5">
                        {r.submitted_at
                          ? `${t('expenses.submittedOn')} ${formatDate(r.submitted_at, i18n.language)}`
                          : t('common.draft')}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Pill tone={STATUS_TONE[r.status]}>{t(`common.${r.status}`)}</Pill>
                      <span className="num font-medium">
                        {formatAmount(r.total_cents, r.currency, i18n.language)}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {wsId && (
        <>
          <ExpenseReportCreateDrawer
            wsId={wsId}
            open={createOpen}
            onOpenChange={setCreateOpen}
            onCreated={(id) => setDrawerId(id)}
          />
          <ExpenseReportDrawer
            wsId={wsId}
            reportId={drawerId}
            open={!!drawerId}
            onOpenChange={(o) => !o && setDrawerId(null)}
          />
        </>
      )}
    </AppShell>
  );
}
